import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { useParams } from 'react-router-dom';
import { Grid, Card, Collapse, CardHeader, LinearProgress } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import Storage from 'src/utils/storage/storage';
import { getEanZrepTableData } from '../../store/planner-slice-async-thunks';
import './table.scss';

// component to update each row of Ean Zrep table
function Row(props: { row: { eanupc_code: string, zrep: string[] } }) {
    const { row } = props;
    return (
        <React.Fragment>
            <tr>
                <td className="row-style">
                    {row.eanupc_code}
                </td>
                <td className="row-style-2">
                    {row.zrep.join(', ')}
                </td>
            </tr>
        </React.Fragment>
    );
}

// ean zrep table component in the drawer view for ppg/sku depending upon the user selection
export default function EanZrepTable({ field, fieldName, productID, productName }) {
    const appDispatch = useAppDispatch();
    const { toggledView } = useAppSelector((state) => state.plannerV2);
    // getting states of ean zrep metadata
    const { isLoading, productMapping } = useAppSelector((state) => state.plannerV2.eanZrepTableMetadata)
    // getting the details of the module ID, scenario ID, source ID 
    const { id: scenarioId } = useParams();
    const { source: sourceId } = Storage.getSourceId();
    const { moduleNameToModuleIdMap } = useAppSelector(
        (state) => state.simulatorHome
    );
    // state to check if the ean zrep table component is open or not 
    const [open, setOpen] = useState(false);
    const [tabularDataHeight, setTabularDataHeight] = useState('100px');

    // if the table is open and the table data is not present in the productMapping
    useEffect(() => {
        if (open && !productMapping[productName]) {
            appDispatch(getEanZrepTableData({
                apiPayload: {
                    [field]: productID,
                    [fieldName]: productName,
                    'toggle': toggledView
                },
                urlParams: {
                    moduleId: moduleNameToModuleIdMap.planner,
                    scenarioId: Number(scenarioId),
                    sourceId
                },
                productName
            }));
        }
    }, [open, productName]);
    // to set the maximum height of the table
    useEffect(() => {
        if (open && productMapping[productName]) {
            setTabularDataHeight('148px');
        }
    }, [open, productMapping])
    // function to calculate the counts of the no. of entries in each column (ean, zrep columns)
    const calculateCounts = (headerField) => {
        if (headerField === "eanupc_code") {
            return productMapping[productName]?.row?.length || 0;
        } else if (headerField === "zrep") {
            return productMapping[productName]?.row?.reduce((acc, row) => acc + row.zrep.length, 0) || 0;
        }
        return 0;
    };

    return (
        <div className='table-component'>
            {/* Grid to show the title and expand icon of the component */}
            <Grid item xs={12} px={0.5} className='title-icon' onClick={() => setOpen(!open)}>
                <Grid item xs={10} className='card-header'>
                    <span>EAN and Zrep Details</span>
                </Grid>
                <Grid item xs={2} className='icon'>
                    <IconButton aria-label="expand" size="small" color="primary">
                        {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </Grid>
            </Grid>
            {/* to render the data only when the user clicks on the title grid */}
            <Collapse in={open} timeout="auto" unmountOnExit>
                {/* loader component to indicate that the data is being fetched through the API call */}
                {open && isLoading && !productMapping[productName] &&
                    <div className='parent-loader-div'>
                        <LinearProgress className='loader-div' />{' '}
                    </div>
                }
                {/* actual table data with the column headers along with the respective counts and the corresponding rows */}
                <div className="tabular-data" style={{ maxHeight: tabularDataHeight }}>
                    <table className="table-style">
                        {productMapping[productName] && Object.keys(productMapping[productName]).length > 0 &&
                            <>
                                <tr>
                                    {productMapping[productName].header.map((header) => (
                                        <th key={header.name} className="header-alias">
                                            {header.alias} ({calculateCounts(header.parent_field)})
                                        </th>
                                    ))}
                                </tr>
                                <tbody>
                                    {productMapping[productName].row.map((row, index) => (
                                        <Row key={index} row={row} />
                                    ))}
                                </tbody>
                            </>
                        }
                    </table>
                </div>
            </Collapse>
        </div>
    );
}